<template>
  <div>
    <div class="modal is-active">
      <loading-component v-if="loading" />
      <div class="modal-background" @click="!loading && close()"></div>
      <div class="modal-content" v-if="!loading">
        <div class="box funnel-modal">

          <h3 class="title is-4 has-text-centered mb-4">
            Seguimiento del embudo
          </h3>
          <div class="columns is-multiline">
            <div
              v-for="field in visibleFields"
              :key="field.key"
              class="column is-6"
            >
              <div class="field">
                <label class="label">{{ field.label }}</label>

                <div v-if="field.type === 'select'" class="control">
                  <div class="select is-fullwidth">
                    <select v-model="formData[field.key]">
                      <option :value="null">Selecciona una opción</option>
                      <option
                        v-for="opt in field.options"
                        :key="opt.slug"
                        :value="opt"
                      >
                        {{ opt.nombre }}
                      </option>
                    </select>
                  </div>
                </div>

                <div v-else-if="field.type === 'multiselect'" class="control">
                  <div class="select is-multiple is-fullwidth">
                    <select multiple v-model="formData[field.key]">
                      <option
                        v-for="opt in field.options"
                        :key="opt.slug"
                        :value="opt"
                      >
                        {{ opt.nombre }}
                      </option>
                    </select>
                  </div>
                  <p class="help">Mantén presionado Ctrl / Cmd para seleccionar múltiples</p>
                </div>

                <div v-else-if="field.type === 'boolean'" class="control">
                  <label class="checkbox">
                    <input type="checkbox" v-model="formData[field.key]" /> Sí
                  </label>
                </div>

                <div v-else-if="field.type === 'textarea'" class="control">
                  <textarea
                    class="textarea"
                    rows="3"
                    v-model="formData[field.key]"
                    placeholder="Escribe aquí..."
                  />
                </div>

                <!-- Radio -->
                <div v-else-if="field.type === 'radio'" class="control canal-options">
                  <div v-for="option in field.options" :key="option.slug" class="control">
                    <label class="radio custom-radio">
                      <input type="radio" :value="option.slug" v-model="formData[field.key]" />
                      <span class="radio-label">{{ option.nombre }}</span>
                    </label>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div v-if="error" class="notification is-danger is-light mt-3">
            {{ error }}
          </div>

          <div class="columns mt-5">
            <div class="column">
              <button
                class="button is-danger is-outlined is-fullwidth is-rounded"
                :disabled="loading"
                @click="close"
              >
                Cancelar
              </button>
            </div>
            <div class="column">
              <button
                class="button is-success is-fullwidth is-rounded"
                :disabled="loading"
                @click="confirm"
              >
                Guardar
              </button>
            </div>
          </div>

        </div>
      </div>
      <button
        class="modal-close is-large"
        aria-label="close"
        :disabled="loading"
        @click="!loading && close()"
      ></button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import {
  TOUCH_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  OVERALL_STATUS_OPTIONS,
  REJECTION_REASON_OPTIONS,
  ACQUISITION_CHANNEL_OPTIONS
} from '@/lib/funnelOptions'

export default {
  components: {
    LoadingComponent: () => import('@/components/ui/Loading')
  },

  props: {
    close: { type: Function, required: true },
    action: { type: Function, required: true },
    detail: { type: Object, required: true }
  },

  data () {
    return {
      loading: false,
      error: '',
      formConfig: [
        {
          key: 'number_of_touches',
          label: 'Cantidad de toques',
          type: 'select',
          options: TOUCH_OPTIONS
        },
        {
          key: 'contact_method',
          label: 'Medio de contacto',
          type: 'multiselect',
          options: CONTACT_METHOD_OPTIONS
        },
        {
          key: 'overall_status',
          label: 'Estado general',
          type: 'select',
          options: OVERALL_STATUS_OPTIONS
        },
        {
          key: 'rejection_reason',
          label: 'Motivo de rechazo',
          type: 'select',
          showWhen: (formData) => formData.overall_status?.slug === 'rechazado',
          options: REJECTION_REASON_OPTIONS
        },
        { key: 'equipment_condition', label: 'Condición física del equipo', type: 'textarea' },
        { key: 'customer_response', label: 'Respuesta del cliente', type: 'textarea' },
        { key: 'is_first_sale', label: '¿Es primera venta?', type: 'boolean' },
        { key: 'is_duplicate_product', label: '¿Es producto duplicado?', type: 'boolean' },
        { key: 'customer_rejected_price', label: '¿El cliente rechazó por precio?', type: 'boolean' },
        {
          key: 'acquisition_channel',
          label: 'Canal de adquisición',
          type: 'select',
          options: ACQUISITION_CHANNEL_OPTIONS
        }
      ],
      formData: {
        number_of_touches: null,
        contact_method: [],
        overall_status: null,
        equipment_condition: '',
        customer_response: '',
        is_first_sale: false,
        is_duplicate_product: false,
        customer_rejected_price: false,
        rejection_reason: null,
        acquisition_channel: null
      }
    }
  },

  computed: {
    visibleFields () {
      return this.formConfig.filter(field => !field.showWhen || field.showWhen(this.formData))
    }
  },

  watch: {
    'formData.overall_status' (status) {
      if (status?.slug !== 'rechazado') {
        this.formData.rejection_reason = null
      }
    }
  },

  async mounted () {
    this.loading = true
    try {
      const data = await this.getAdminUpsertFunnelTracking({ productId: this.detail.id })
      const payload = data || {}

      const mapSelect = (fieldKey) => {
        const field = this.formConfig.find(f => f.key === fieldKey)
        return field.options.find(opt => opt.slug === payload[fieldKey]) || null
      }

      const mapMulti = (fieldKey) => {
        const field = this.formConfig.find(f => f.key === fieldKey)
        return Array.isArray(payload[fieldKey])
          ? payload[fieldKey].map(slug => field.options.find(opt => opt.slug === slug)).filter(Boolean)
          : []
      }

      this.formData = {
        number_of_touches: mapSelect('number_of_touches'),
        contact_method: mapMulti('contact_method'),
        overall_status: mapSelect('overall_status'),
        equipment_condition: payload.equipment_condition ?? '',
        customer_response: payload.customer_response ?? '',
        is_first_sale: payload.is_first_sale ?? false,
        is_duplicate_product: payload.is_duplicate_product ?? false,
        customer_rejected_price: payload.customer_rejected_price ?? false,
        rejection_reason: mapSelect('rejection_reason'),
        acquisition_channel: mapSelect('acquisition_channel')
      }
    } catch (e) {
      console.error('Error fetching funnel tracking:', e)
      this.error = 'No se pudo cargar el seguimiento.'
    } finally {
      this.loading = false
    }
  },

  methods: {
    ...mapActions(['getAdminUpsertFunnelTracking']),

    async confirm () {
      this.error = ''
      this.loading = true

      try {
        const payload = {
          ...this.formData,
          number_of_touches: this.formData.number_of_touches?.slug || null,
          overall_status: this.formData.overall_status?.slug || null,
          rejection_reason: this.formData.rejection_reason?.slug || null,
          acquisition_channel: this.formData.acquisition_channel?.slug || null,
          contact_method: this.formData.contact_method.map(item => item.slug)
        }

        await this.action(payload)
        this.close()
      } catch (e) {
        this.error = 'No se pudo guardar el seguimiento.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.funnel-modal {
  max-width: 700px;
  margin: auto;
}

.select.is-multiple select {
  min-height: 120px;
}
</style>
